"use client";

import { useState } from "react";

import { detectFileKind, type FileKind, FilePreview } from "./FilePreview";

type Props = {
    /** 実体(Blob)を取得する。プレビューは全バイトを要するため、押下時に初めて呼ぶ。 */
    load: () => Promise<Blob>;
    fileName?: string;
    contentType?: string;
    /** 指定スライドへ送る要求（pptx のみ・1 始まり）。未表示のうちは効かない。 */
    slideRequest?: { slideNo: number; nonce: number } | null;
};

/**
 * 種別ごとの既定表示高さ(初期値)。ファイルの縦横比/情報量に合わせる
 * (横長スライドは低め、縦長の文書/PDF は高め、非対応は最小)。いずれも `resize-y` で伸縮可能。
 */
const HEIGHT_BY_KIND: Record<FileKind, string> = {
    pptx: "h-[600px]",
    docx: "h-[900px]",
    pdf: "h-[900px]",
    xlsx: "h-[640px]",
    csv: "h-[480px]",
    image: "h-[480px]",
    text: "h-[480px]",
    unknown: "h-40", // 160px。アイコン＋DL のみ
};

/**
 * プレビューの取得契機。**常にボタン起動**＝明示操作でのみ実体を取得して描画する
 * (帯域・成果物のスクラブ処理を無駄に走らせない)。取得後は FilePreview が種別を判定して描く
 * (画像/PDF/テキスト/CSV は内蔵、docx/xlsx/pptx は WASM を遅延ロード)。
 *
 * Web(amplify)・アドイン双方から使う共有部品。データ取得(presigned→blob)は `load` に注入する。
 */
export function FilePreviewLoader({ load, fileName, contentType, slideRequest }: Props) {
    const [blob, setBlob] = useState<Blob | null>(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const onShow = async () => {
        setBusy(true);
        setErr(null);
        try {
            setBlob(await load());
        } catch (e) {
            setErr(e instanceof Error ? e.message : "プレビューの取得に失敗しました");
        } finally {
            setBusy(false);
        }
    };

    if (blob) {
        // 既定高さは種別ごと。`resize-y` + `overflow-hidden` で縦に伸縮できる。
        // (Textarea は自動伸長へ変えたため掴みを持たない。ここだけが手で広げる欄)
        const height = HEIGHT_BY_KIND[detectFileKind(contentType, fileName)];
        return (
            <div className={`mt-2 ${height} min-h-24 resize-y overflow-hidden`}>
                <FilePreview
                    blob={blob}
                    fileName={fileName}
                    contentType={contentType}
                    slideRequest={slideRequest}
                />
            </div>
        );
    }
    // 未取得時は「表示領域」だと分かる高さのプレースホルダ。文言は上下左右センタリング。
    // 配色はビューUIの「もっと読む」に合わせる(破線 interactive 枠＋interactive 文字＋淡い hover)。
    return (
        <div className="mt-2 flex flex-col gap-1">
            <button
                type="button"
                disabled={busy}
                onClick={onShow}
                className="flex h-48 w-full items-center justify-center gap-1.5 rounded border border-dashed border-interactive/50 text-sm text-interactive transition-colors hover:bg-interactive/5 disabled:opacity-50"
            >
                {busy ? "読み込み中…" : "プレビューを表示"}
            </button>
            {err && <span className="text-xs text-danger">{err}</span>}
        </div>
    );
}
